import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { EnhancedTabs } from '@swyftx/aviary/molecules/EnhancedTabs';
import { MarginOrderDirection, MarginOrderType } from '@swyftx/margin/build/types';

import { DATA_FIELDS, SYMBOL } from './OpeningPositionsContentSample.data';

type Props = {
  marginDirection?: MarginOrderDirection;
  marginType?: MarginOrderType;
};

const OpeningPositionsContentSample: React.FC<Props> = ({ marginDirection, marginType }) => {
  const [direction, setDirection] = useState<MarginOrderDirection>(marginDirection || MarginOrderDirection.Long);
  const [type, setType] = useState<MarginOrderType>(marginType || MarginOrderType.Market);
  const { t } = useTranslation('margin');

  return (
    <FlexLayout className='rounded-lg bg-color-background-surface-tertiary p-16' direction='column' spacing={24}>
      <Heading size='h5'>{t('understandingOrderTypes.title.open')}</Heading>
      <FlexLayout className='flex-wrap' spacing={12}>
        <FlexLayout direction='column' spacing={4} className='w-auto'>
          <Body size='small' weight='bold'>
            {t('understandingOrderTypes.labels.orderDirection')}
          </Body>
          <EnhancedTabs
            variant='default'
            value={direction.toString()}
            tabs={[
              { title: t(`tradeSide.${MarginOrderDirection.Long}`), value: MarginOrderDirection.Long.toString() },
              { title: t(`tradeSide.${MarginOrderDirection.Short}`), value: MarginOrderDirection.Short.toString() },
            ]}
            onChange={(val) => setDirection(Number(val))}
          />
        </FlexLayout>
        <FlexLayout direction='column' spacing={4} className='w-auto'>
          <Body size='small' weight='bold'>
            {t('understandingOrderTypes.labels.orderType')}
          </Body>
          <EnhancedTabs
            variant='default'
            value={type.toString()}
            tabs={[
              { title: t(`tradeType.${MarginOrderType.Market}`), value: MarginOrderType.Market.toString() },
              { title: t(`tradeType.${MarginOrderType.Limit}`), value: MarginOrderType.Limit.toString() },
              { title: t(`tradeType.${MarginOrderType.Stop}`), value: MarginOrderType.Stop.toString() },
            ]}
            onChange={(val) => setType(Number(val))}
          />
        </FlexLayout>
      </FlexLayout>
      {DATA_FIELDS[direction][type].illustration}
      <FlexLayout direction='column' spacing={8}>
        <Heading size='h5'>{t('understandingOrderTypes.labels.scenario')}</Heading>
        <Body color='secondary'>
          {t(`understandingOrderTypes.open.${direction}.${type}.scenario`, {
            symbol: SYMBOL,
            currentPrice: DATA_FIELDS[direction][type].currentPrice,
            targetPrice: DATA_FIELDS[direction][type].targetPrice,
            type: t(`tradeType.${type}`).toLowerCase(),
            direction: t(`tradeSide.${direction}`).toLowerCase(),
          })}
        </Body>
      </FlexLayout>

      <FlexLayout direction='column' spacing={8}>
        <Heading size='h5'>{t(`understandingOrderTypes.open.${direction}.priceTrendsUpwards`)}</Heading>
        <Body color='secondary'>
          {t(`understandingOrderTypes.open.${direction}.${type}.priceTrendsUpwards`, {
            symbol: SYMBOL,
          })}
        </Body>
      </FlexLayout>

      <FlexLayout direction='column' spacing={8}>
        <Heading size='h5'>{t(`understandingOrderTypes.open.${direction}.priceTrendsDownwards`)}</Heading>
        <Body color='secondary'>
          {t(`understandingOrderTypes.open.${direction}.${type}.priceTrendsDownwards`, {
            symbol: SYMBOL,
          })}
        </Body>
      </FlexLayout>
      {t(`understandingOrderTypes.open.${direction}.${type}.note`) && (
        <Body color='secondary'>
          {t(`understandingOrderTypes.open.${direction}.${type}.note`, {
            type: t(`tradeType.${type}`).toLowerCase(),
          })}
        </Body>
      )}
    </FlexLayout>
  );
};

export { OpeningPositionsContentSample };
